import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuClock } from 'react-icons/lu';
import Badge from '../../../../components/ui/Badge';
import useSalaryPaymentText from '../salaryPaymentText';
import { MONEY_KEYS } from '../salaryPaymentColumns';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

export const initialsOf = (salary) => `${(salary.employeeName || '').charAt(0)}${(salary.employeeSurname || '').charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';

export const PAID_GRADIENT = {
  true: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
  false: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30',
};

/** Ödendi / Ödenmedi for the chosen month. */
export const PaidBadge = ({ paid }) => {
  const { label } = useSalaryPaymentText();
  return paid ? (
    <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{label('status.paid')}</Badge>
  ) : (
    <Badge tone="warning"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{label('status.unpaid')}</Badge>
  );
};

PaidBadge.propTypes = { paid: PropTypes.bool };

/** Tooltip / plain text of a cell. */
export const useSalaryCellText = () => {
  const { label, formatMoney, formatDate } = useSalaryPaymentText();
  return (salary, key) => {
    if (MONEY_KEYS.includes(key)) return formatMoney(salary.view[key]);
    if (key === 'isCurrentMonthPaid') return salary.isCurrentMonthPaid ? label('status.paid') : label('status.unpaid');
    if (key === 'lastPaymentDate' || key === 'contractDate') return formatDate(salary[key]);
    return salary[key] ? String(salary[key]) : '';
  };
};

/** Content of one salary payment table cell. */
const SalaryPaymentCell = ({ columnKey, salary }) => {
  const cellText = useSalaryCellText();
  if (columnKey === 'isCurrentMonthPaid') return <PaidBadge paid={salary.isCurrentMonthPaid} />;
  if (columnKey === 'employeeName') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${PAID_GRADIENT[Boolean(salary.isCurrentMonthPaid)]}`}>
          {initialsOf(salary)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{salary.employeeName || '-'}</span>
      </span>
    );
  }
  const text = cellText(salary, columnKey);
  if (!text) return <span className={MUTED}>-</span>;
  const strong = columnKey === 'employeeSurname' || columnKey === 'netSalary';
  const negative = ['sskEmployeeShare', 'unemploymentEmployeeShare', 'stampTax', 'totalDeductions'].includes(columnKey);
  return (
    <span className={`tw-block tw-truncate ${MONEY_KEYS.includes(columnKey) ? 'tw-tabular-nums' : ''} ${strong ? 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' : ''} ${negative ? 'tw-text-rose-600/90 dark:tw-text-rose-300/90' : ''}`}>
      {text}
    </span>
  );
};

SalaryPaymentCell.propTypes = { columnKey: PropTypes.string.isRequired, salary: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const SalaryPaymentCardRow = ({ salary }) => {
  const { label, formatMoney, formatDate } = useSalaryPaymentText();
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${PAID_GRADIENT[Boolean(salary.isCurrentMonthPaid)]}`}>
        {initialsOf(salary)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${salary.employeeName} ${salary.employeeSurname}`.trim() || '-'}</span>
          <PaidBadge paid={salary.isCurrentMonthPaid} />
        </span>
        <span className="tw-mt-0.5 tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-text-[13px]">
          <span className="tw-truncate tw-text-slate-500 dark:tw-text-slate-400">
            {salary.lastPaymentDate ? label('card.paidOn', { date: formatDate(salary.lastPaymentDate) }) : label('card.net')}
          </span>
          <span className="tw-shrink-0 tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{formatMoney(salary.view.netSalary)}</span>
        </span>
      </span>
    </>
  );
};

SalaryPaymentCardRow.propTypes = { salary: PropTypes.object.isRequired };

export default SalaryPaymentCell;
